import React, { useEffect, useRef, useState } from 'react';
import { GenericObj } from '@solvhealth/types/interfaces/generics';
// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { createForm, formShape } from 'rc-form';
import isEqual from 'lodash/isEqual';
import { useDispatch } from 'react-redux';
import omit from 'lodash/omit';
import { v4 as uuid } from 'uuid';
import { deleteNullFields, sanitizeForm } from '../../../../core/util/form';
import PaperworkForm from '../../PaperworkForm';
import { PageNumber, SectionPage } from '~/components/Paperwork/interfaces';
import { usePreloadedData } from '../../hooks/usePreloadedData';
import PaperworkFields from '../../util/PaperworkFields';
import { useSolvSelector } from '~/reducers';
import { PatientPageNumber } from './constants';
import { PaperworkFieldName } from '../../paperworkConstants';
import { apiPostJson } from '../../../../core/dapi';
import { DAPI_HOST } from '../../../../config';
import {
  addAddressValidationLog,
  setAddressValidationError,
  setIsAddressValidated,
  setValidatedAddress as setPaperworkValidatedAddress,
} from '~/ducks/paperwork';
import { useShouldShowAddressValidation } from './hooks/useShouldShowAddressValidation';
import { AddressValidationSessionTokenContext } from './AddressValidationSessionTokenContext';
import { Address, AddressValidation } from './types';
import { SuggestedAddressModal } from './SuggestedAddressModal';
import { getValidationDiffs } from './util';

type Props = {
  form: formShape;
  onGoForward: () => void;
  handleClickNext: (newForm: GenericObj, extraFields: GenericObj) => void;
  sectionPage: SectionPage;
  pageInSection: PageNumber;
};

const getAddressFieldValues = (form: formShape) => {
  return {
    address1:
      (form.getFieldValue(PaperworkFieldName.PATIENT_ADDRESS_STREET) as string)?.trim() ?? '',
    address2:
      (form.getFieldValue(PaperworkFieldName.PATIENT_ADDRESS_STREET_TWO) as string)?.trim() ?? '',
    city: (form.getFieldValue(PaperworkFieldName.PATIENT_ADDRESS_CITY) as string)?.trim() ?? '',
    state: (form.getFieldValue(PaperworkFieldName.PATIENT_ADDRESS_STATE) as string)?.trim() ?? '',
    zip: (form.getFieldValue(PaperworkFieldName.PATIENT_ADDRESS_ZIP) as string)?.trim() ?? '',
  };
};

const PatientSectionForm = (props: Props) => {
  const { form, handleClickNext, onGoForward, sectionPage, pageInSection } = props;

  const [currentAddress, setCurrentAddress] = useState({});
  const [subpremiseWarning, setSubpremiseWarning] = useState(false);
  const [showSubpremiseWarning, setShowSubpremiseWarning] = useState(false);
  const [validatedAddress, setValidatedAddress] = useState({} as Address);
  const [suggestedAddressModalOpen, setSuggestedAddressModalOpen] = useState(false);
  const [diffs, setDiffs] = useState({});

  // keep track of if the suggested address modal was opened so we can know whether or not to log skip-fixed-typos
  const suggestedAddressModalOpened = useRef(false);

  const paperwork = useSolvSelector((state) => state.paperwork);
  const location = useSolvSelector((state) => state.location);
  const dispatch = useDispatch();
  const preloadedData = usePreloadedData();
  const subpremiseValue = form.getFieldValue(PaperworkFieldName.PATIENT_ADDRESS_STREET_TWO);

  const isAddressValidationEnabled = useShouldShowAddressValidation(location.id ?? '');

  const [sessionToken, setSessionToken] = useState(uuid());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [paperwork.pageView]);

  useEffect(() => {
    if (pageInSection === PatientPageNumber.Address) {
      const submittingAddress = getAddressFieldValues(form);
      if (
        !isEqual(submittingAddress, currentAddress) &&
        subpremiseWarning &&
        showSubpremiseWarning
      ) {
        /**
         * Hide subpremise warning if address is changed, but don't remove the warning
         * if Mary enters a new address, it'll be re-validated and might remove the warning from there
         * but if Mary ends up entering the same address, we'll want to know that the warning existed so
         * we can compare and see if she added the subpremise
         */
        setShowSubpremiseWarning(false);
      }
    }
  }, [form, currentAddress, subpremiseWarning, showSubpremiseWarning, pageInSection]);

  useEffect(() => {
    setShowSubpremiseWarning(subpremiseWarning);
  }, [subpremiseWarning]);

  const validateForm = () => {
    form.validateFields(async (errors: any, values: any) => {
      if (!errors) {
        let extraFields = {};
        if (pageInSection === PatientPageNumber.Address && isAddressValidationEnabled) {
          let currentValidatedAddress = validatedAddress;
          let currentHasSubpremiseWarning = subpremiseWarning;
          let isSubmittingAddressValidated = !!paperwork.isAddressValidated;
          let shouldLogSkipFixTypos = !suggestedAddressModalOpened.current;
          const submittingAddress = getAddressFieldValues(form);
          /**
           * If the submittingAddress is equal to the validatedAddress in state and isAddressValidated is True,
           * do not re-validate.
           * This happens when the address is autofilled and already validated, or has already been filled out/validated
           * this paperwork session and shouldn't be re-validated when moving forward + backward through paperwork
           */
          if (
            !isEqual(paperwork.validatedAddress, submittingAddress) ||
            !paperwork.isAddressValidated
          ) {
            if (!isEqual(submittingAddress, currentAddress)) {
              setCurrentAddress(submittingAddress);
              try {
                const res = await apiPostJson<AddressValidation>(
                  `${DAPI_HOST}/v1/address/location/${location.id}/validate`,
                  {
                    address: Object.values(submittingAddress).filter(
                      (addressLine) => addressLine.length
                    ),
                    location_id: location.id,
                    session_token: sessionToken,
                  }
                );
                if (res?.data?.address) {
                  setValidatedAddress(res.data.address);
                  currentValidatedAddress = res.data.address;
                }
                /**
                 * We block Mary once from proceeding if the address she has entered is missing a subpremise.
                 * If she clicks "continue" a second time, she will override the warning and proceed anyways
                 */
                if (
                  res?.data?.missing_components &&
                  res.data.missing_components.includes('subpremise')
                ) {
                  setSubpremiseWarning(true);
                  currentHasSubpremiseWarning = true;
                  return;
                }
                /**
                 * If the addresses are the same except for the subpremise, we know that Mary was warned to enter a subpremise and then
                 * entered it. In that case, we want to keep the subprmise warning on because we'll need to know it once existed when
                 * adding address validation logs.
                 * If the addresses are different, then we're validating a whole new address and want to reset the subpremise warning.
                 */
                if (
                  !isEqual(
                    omit(submittingAddress, ['address2']),
                    omit(currentAddress, ['address2'])
                  )
                ) {
                  setSubpremiseWarning(false);
                  currentHasSubpremiseWarning = false;
                }
              } catch (e) {
                dispatch(
                  addAddressValidationLog({
                    correction_context: 'paperwork',
                    correction_type: 'error-aborted',
                    validation_successful: false,
                  })
                );
                shouldLogSkipFixTypos = false;
                dispatch(setAddressValidationError(true));
              }
            }
            if (Object.keys(currentValidatedAddress).length) {
              const currentDiffs = getValidationDiffs(submittingAddress, currentValidatedAddress);
              /**
               * If the suggested address is missing any of these components, consider it an invalid address
               * validation and skip the suggested address modal
               */
              const suggestedAddressIsNotValid =
                !currentValidatedAddress.address1 ||
                !currentValidatedAddress.city ||
                !currentValidatedAddress.state ||
                !currentValidatedAddress.zip;

              setDiffs(currentDiffs);
              if (
                currentDiffs.hasChanges &&
                !suggestedAddressModalOpen &&
                !suggestedAddressIsNotValid
              ) {
                setSuggestedAddressModalOpen(true);
                suggestedAddressModalOpened.current = true;
                return;
              }

              const stillHasSubpremiseError = currentHasSubpremiseWarning && !subpremiseValue;
              /**
               * Address is considered valid as long as:
               * it matches the validated address returned from /v1/address/location/<location_id>/validate
               * if there was a subpremise error, an address line 2 has been provided
               * the suggested address was not missing any components so we were able to show the suggested address modal if appropriate
               * */
              const isAddressValidated = !!(
                !stillHasSubpremiseError &&
                !currentDiffs.hasChanges &&
                !suggestedAddressIsNotValid
              );
              isSubmittingAddressValidated = isAddressValidated;
              extraFields = {
                isAddressValidated,
              };
              dispatch(setIsAddressValidated(isAddressValidated));
              if (isAddressValidated) {
                dispatch(setPaperworkValidatedAddress(submittingAddress));
              }
            }
            if (currentHasSubpremiseWarning) {
              dispatch(
                addAddressValidationLog({
                  correction_context: 'paperwork',
                  correction_type: 'subpremise',
                  validation_successful: !!subpremiseValue,
                })
              );
            }
          }
          if (shouldLogSkipFixTypos) {
            /**
             * Add skip-fixed-typos to addressValidationLogs as long as no error-aborted's were encountered and the suggestedAddress
             * wasn't missing any components
             */
            dispatch(
              addAddressValidationLog({
                correction_context: 'paperwork',
                correction_type: 'skip-fixed-typos',
                validation_successful: isSubmittingAddressValidated,
              })
            );
          }
        }
        const newForm = sanitizeForm(deleteNullFields(values));
        handleClickNext(newForm, extraFields);
      }

      return;
    });
  };

  const handleNextButtonClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm();
  };

  return (
    <AddressValidationSessionTokenContext.Provider value={{ sessionToken, setSessionToken }}>
      {isAddressValidationEnabled && (
        <SuggestedAddressModal
          diffs={diffs}
          form={form}
          onClose={() => {
            setSuggestedAddressModalOpen(false);
            /**
             * set suggestedAddressModalOpened to false, if it's opened again it'll be reset to true
             * but they basically ignored the modal if they closed it, so we should log
             * skip-fixed-typos if it's not opened again (because they correct their address themselves)
             */
            suggestedAddressModalOpened.current = false;
          }}
          onContinue={(usedValidatedAddress: boolean) => {
            setSuggestedAddressModalOpen(false);
            if (usedValidatedAddress) {
              const submittingAddress = getAddressFieldValues(form);
              setCurrentAddress(submittingAddress);
            }
            validateForm();
          }}
          open={suggestedAddressModalOpen}
          validatedAddress={validatedAddress}
        />
      )}
      <PaperworkForm
        form={form}
        onSkip={onGoForward}
        onSubmit={handleNextButtonClick}
        sectionPage={sectionPage}
      >
        <PaperworkFields
          fieldNames={sectionPage.fieldNames}
          form={form}
          handleSetWarning={(fieldName: string) => {
            if (
              fieldName === PaperworkFieldName.PATIENT_ADDRESS_STREET_TWO &&
              showSubpremiseWarning &&
              !subpremiseValue
            ) {
              return 'Missing apt/suite/unit number?';
            }
            return false;
          }}
          preloadedData={preloadedData}
        />
      </PaperworkForm>
    </AddressValidationSessionTokenContext.Provider>
  );
};

export default createForm()(PatientSectionForm);
